import { currencies } from "@megaron/catalog-contracts";
import { HttpAction } from "@megaron/http-service";
import { Serializers, SerializerValue } from "@megaron/serializers";
import { GetOrdersQuery, OrderDisplayFinancialStatus, OrderDisplayFulfillmentStatus } from "@megaron/shopify";

const packageItemSerializer = Serializers.object({
  lineItemId: Serializers.string,
  name: Serializers.string,
  variantId: Serializers.string,
  productId: Serializers.string,
  quantity: Serializers.integer,
  weight: Serializers.decimal,
  barcode: Serializers.string,
  price: Serializers.decimal,
  engraving: Serializers.string.nullable(),
});

export type PackageItem = SerializerValue<typeof packageItemSerializer>;

const refundItemSerializer = Serializers.object({
  lineItemId: Serializers.string,
  name: Serializers.string,
  variantId: Serializers.string,
  productId: Serializers.string,
  quantity: Serializers.integer,
  amount: Serializers.decimal,
});

export type RefundItem = SerializerValue<typeof refundItemSerializer>;

const refundSerializer = Serializers.object({
  id: Serializers.string,
  createdAt: Serializers.datetime,
  totalAmount: Serializers.decimal,
  items: refundItemSerializer.array(),
});

export type Refund = SerializerValue<typeof refundSerializer>;

export const packageSize = ["small", "medium", "large", "custom"] as const;
export type PackageSize = (typeof packageSize)[number];

export const trackingStatuses = [
  "created",
  "confirmed",
  "stored_by_sender",
  "dispatched_by_sender",
  "collected_from_sender",
  "adopted_at_source_branch",
  "sent_from_source_branch",
  "adopted_at_sorting_center",
  "sent_from_sorting_center",
  "adopted_at_target_branch",
  "out_for_delivery",
  "ready_to_pickup",
  "pickup_reminder_sent",
  "pickup_time_expired",
  "undelivered",
  "returned_to_sender",
  "delivered",
  "canceled",
  "out_for_delivery_to_address",
  "claimed",
] as const;
export type TrackingStatus = (typeof trackingStatuses)[number];

export const orderStatusValues = [
  "fulfilled",
  "paid",
  "pending",
  "cancelled",
  "refunded",
  "partially_fulfilled",
  "unfulfilled",
  "unknown",
] as const;

export const deliveryMethod = ["inPostLocker", "inPostCourier", "palletDelivery", "shipping", "other"] as const;
export type DeliveryMethod = (typeof deliveryMethod)[number];

type ShopifyFulfillment = NonNullable<GetOrdersQuery["orders"]["edges"][number]["node"]["displayFulfillmentStatus"]>;
type ShopifyFinancial = NonNullable<GetOrdersQuery["orders"]["edges"][number]["node"]["displayFinancialStatus"]>;

const enumToStringValues = <T extends Record<string, string | number>>(e: T) =>
  Object.values(e).filter((v): v is Extract<T[keyof T], string> => typeof v === "string");

export const fulfillmentStatus = enumToStringValues(OrderDisplayFulfillmentStatus) as readonly ShopifyFulfillment[];
export const financialStatus = enumToStringValues(OrderDisplayFinancialStatus) as readonly ShopifyFinancial[];

const packageSerializer = Serializers.object({
  id: Serializers.string,
  orderId: Serializers.string,
  name: Serializers.string,
  items: packageItemSerializer.array(),
  size: Serializers.stringOneOf(...packageSize).nullable(),
  isLabelScanned: Serializers.boolean,
  trackingNumber: Serializers.string.nullable(),
  shipmentId: Serializers.string.nullable(),
  trackingStatus: Serializers.stringOneOf(...trackingStatuses).nullable(),
  dispatchOrderId: Serializers.integer.nullable(),
});

export type Package = SerializerValue<typeof packageSerializer>;

export const orderSerializer = Serializers.object({
  id: Serializers.string,
  orderNumber: Serializers.integer,
  processedAt: Serializers.datetime,
  email: Serializers.string,
  deliveryMethod: Serializers.stringOneOf(...deliveryMethod),
  fulfillmentStatus: Serializers.stringOneOf(...fulfillmentStatus),
  financialStatus: Serializers.stringOneOf(...financialStatus),
  packages: packageSerializer.array(),
  receiver: Serializers.object({
    name: Serializers.string,
    address1: Serializers.string,
    address2: Serializers.string,
    city: Serializers.string,
    zip: Serializers.string,
    country: Serializers.string,
    email: Serializers.string,
    phone: Serializers.string,
  }),
  items: packageItemSerializer.array(),
  lockerCode: Serializers.string.nullable(),
  cancelledAt: Serializers.datetime.nullable(),
  userId: Serializers.string.nullable(),
  deliveryPrice: Serializers.decimal,
  totalPrice: Serializers.decimal,
  currency: Serializers.stringOneOf(...currencies),
  refunds: refundSerializer.array(),
});

const schedulePickupBodySerializer = Serializers.object({
  name: Serializers.string,
  phone: Serializers.string,
  address: Serializers.object({
    street: Serializers.string,
    buildingNumber: Serializers.string,
    city: Serializers.string,
    postCode: Serializers.string,
    countryCode: Serializers.string,
  }),
  email: Serializers.string,
  comment: Serializers.string,
  officeHours: Serializers.string,
});

export type SchedulePickupBody = SerializerValue<typeof schedulePickupBodySerializer>;

export type Order = SerializerValue<typeof orderSerializer>;

export const orderActions = {
  syncShopifyOrders: HttpAction({
    path: "/syncShopifyOrders",
    method: "post",
    requiresAuth: true as const,
    valueSerializer: Serializers.object({
      inserted: Serializers.integer,
      updated: Serializers.integer,
      scanned: Serializers.integer,
    }),
  }),

  ordersQuery: HttpAction({
    path: "/orders",
    method: "get",
    qsSerializer: Serializers.object({
      limit: Serializers.integer,
      offset: Serializers.integer,
      fulfillmentStatus: Serializers.string.array().asJsonString(),
      financialStatus: Serializers.string.array().asJsonString(),
      deliveryMethod: Serializers.string.array().asJsonString(),
      isCancelled: Serializers.boolean,
      userId: Serializers.string,
    }).partial(),
    requiresAuth: true as const,
    valueSerializer: Serializers.object({ items: orderSerializer.array(), count: Serializers.integer }),
  }),

  userOrdersQuery: HttpAction({
    path: "/userOrdersQuery",
    method: "get",
    qsSerializer: Serializers.object({
      limit: Serializers.integer,
      offset: Serializers.integer,
    }).partial(),
    valueSerializer: Serializers.object({ items: orderSerializer.array(), count: Serializers.integer }),
  }),

  getOrder: HttpAction({
    path: "/order/:orderId",
    method: "get",
    paramsSerializer: Serializers.object({
      orderId: Serializers.string,
    }),
    requiresAuth: true as const,
    valueSerializer: orderSerializer,
  }),

  getUserOrder: HttpAction({
    path: "/userOrder/:orderId",
    method: "get",
    paramsSerializer: Serializers.object({
      orderId: Serializers.string,
    }),
    valueSerializer: orderSerializer,
  }),

  saveOrderPackage: HttpAction({
    path: "/order/:orderId/package",
    method: "put",
    paramsSerializer: Serializers.object({ orderId: Serializers.string }),
    requiresAuth: true as const,
    bodySerializer: Serializers.object({ package: packageSerializer }),
  }),

  removeOrderPackage: HttpAction({
    path: "/order/:orderId/package/:packageId",
    method: "delete",
    paramsSerializer: Serializers.object({ orderId: Serializers.string, packageId: Serializers.string }),
    requiresAuth: true as const,
  }),

  generateLabelForPackage: HttpAction({
    path: "/order/label",
    method: "post",
    requiresAuth: true as const,
    bodySerializer: Serializers.object({
      packageId: Serializers.string,
    }),
    valueSerializer: Serializers.object({
      labelBase64: Serializers.string,
    }),
  }),

  onShopifyOrderCreated: HttpAction({
    path: "/order/onShopifyOrderCreated",
    method: "post",
    bodySerializer: Serializers.object({
      orderNumber: Serializers.string,
      orderId: Serializers.string,
    }),
  }),

  schedulePickupForUndispatched: HttpAction({
    path: "/order/schedulePickupUndispatched",
    method: "post",
    bodySerializer: schedulePickupBodySerializer,
    valueSerializer: Serializers.object({
      dispatchOrderId: Serializers.integer,
      shipmentsCount: Serializers.integer,
    }),
    requiresAuth: true as const,
  }),

  schedulePickupForOrder: HttpAction({
    path: "/order/:orderId/schedulePickup",
    method: "post",
    paramsSerializer: Serializers.object({ orderId: Serializers.string }),
    bodySerializer: schedulePickupBodySerializer,
    valueSerializer: Serializers.object({
      dispatchOrderId: Serializers.integer,
      shipmentsCount: Serializers.integer,
    }),
    requiresAuth: true as const,
  }),

  refreshOrdersInpostStatuses: HttpAction({
    path: "/order/refreshInpostStatuses",
    method: "post",
    requiresAuth: true as const,
    valueSerializer: Serializers.object({
      checked: Serializers.integer,
      updated: Serializers.integer,
    }),
  }),

  addOrderTrackingToShopify: HttpAction({
    path: "/order/addTrackingToShopify",
    method: "post",
    requiresAuth: true as const,
    bodySerializer: Serializers.object({
      orderId: Serializers.string,
    }),
    valueSerializer: Serializers.object({
      checked: Serializers.integer,
      updated: Serializers.integer,
    }),
  }),

  proRewardsToSheets: HttpAction({
    path: "/order/proRewardsToSheets",
    method: "post",
    requiresAuth: true as const,
    bodySerializer: Serializers.object({
      sheetId: Serializers.string,
    }),
  }),

  syncProRewardEngravingFromShopify: HttpAction({
    path: "/order/syncProRewardEngravingFromShopify",
    method: "post",
    requiresAuth: true as const,
    valueSerializer: Serializers.object({
      ordersScanned: Serializers.integer,
      itemsUpdated: Serializers.integer,
    }),
  }),
};
